import React, { useMemo, useState } from 'react';
import { ClipboardList, Search } from 'lucide-react';
import { ProjectRow } from './ProjectRow';
import { money } from './fabDashUtils';

const FILTERS = [
  { id: 'all', label: 'All', test: () => true },
  { id: 'in_progress', label: 'In progress', test: (p) => p.status === 'in_progress' },
  { id: 'waiting', label: 'Waiting', test: (p) => p.flags.includes('waiting') || ['quoted', 'awarded', 'ready_for_scheduling', 'scheduled'].includes(p.status) },
  { id: 'risk', label: 'At risk', test: (p) => p.flags.some((f) => ['late', 'over_budget', 'over_estimate', 'due_soon', 'budget_80'].includes(f)) },
  { id: 'unlisted', label: 'Not on scheduler', test: (p) => !p.on_board },
];
const SORTS = { priority: null, due: (a, b) => (a.required_completion_date || '9999').localeCompare(b.required_completion_date || '9999'),
  cost: (a, b) => (b.cost.total_cost || 0) - (a.cost.total_cost || 0), pct: (a, b) => (b.budget_pct || -1) - (a.budget_pct || -1) };

export const ProjectBoard = ({ projects, costVisible, fabRate }) => {
  const [filter, setFilter] = useState('all');
  const [sort, setSort] = useState('priority');
  const [q, setQ] = useState('');
  const shown = useMemo(() => {
    const f = FILTERS.find((x) => x.id === filter).test;
    const needle = q.trim().toLowerCase();
    const list = projects.filter((p) => f(p) && (!needle || `${p.work_order_number} ${p.customer_name} ${p.project_name} ${p.crew.join(' ')}`.toLowerCase().includes(needle)));
    return SORTS[sort] ? [...list].sort(SORTS[sort]) : list;
  }, [projects, filter, sort, q]);
  const counts = useMemo(() => Object.fromEntries(FILTERS.map((f) => [f.id, projects.filter(f.test).length])), [projects]);
  const shownCost = shown.reduce((s, p) => s + (p.cost.total_cost || 0), 0);

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm" data-testid="fab-project-board">
      <div className="px-4 py-3 border-b border-slate-200 flex flex-col lg:flex-row lg:items-center gap-3">
        <h3 className="text-sm font-semibold text-slate-900 flex items-center gap-2 shrink-0"><ClipboardList className="w-4 h-4 text-blue-700" /> Shop projects & milestones</h3>
        <div className="flex flex-wrap items-center gap-1.5 lg:ml-2">
          {FILTERS.map((f) => (
            <button key={f.id} onClick={() => setFilter(f.id)} data-testid={`project-filter-${f.id}`}
              className={`text-xs rounded-full px-2.5 py-1 border font-medium transition-colors ${filter === f.id ? 'bg-white text-slate-900 border-slate-200' : 'bg-white text-slate-600 border-slate-200 hover:border-slate-400'}`}>
              {f.label} <span className={filter === f.id ? 'text-slate-700' : 'text-slate-500'}>{counts[f.id]}</span>
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2 lg:ml-auto">
          <label className="relative"><Search className="w-3.5 h-3.5 text-slate-500 absolute left-2 top-1/2 -translate-y-1/2" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="WO, customer, person" data-testid="project-search"
              className="pl-7 pr-2 py-1.5 text-xs border border-slate-200 rounded-lg w-44 focus:outline-none focus:border-blue-500" /></label>
          <select value={sort} onChange={(e) => setSort(e.target.value)} data-testid="project-sort" className="text-xs border border-slate-200 rounded-lg py-1.5 px-2 bg-white text-slate-700">
            <option value="priority">Live → priority → due</option><option value="due">Due date</option>
            {costVisible && <option value="cost">Running cost</option>}{costVisible && <option value="pct">% of value spent</option>}
          </select>
        </div>
      </div>
      {shown.length === 0 ? (
        <p className="px-4 py-8 text-sm text-slate-500 text-center">Nothing here{q ? ` for "${q}"` : ''}</p>
      ) : (
        <div className="divide-y divide-slate-100">{shown.map((p) => <ProjectRow key={p.id} p={p} costVisible={costVisible} fabRate={fabRate} />)}</div>
      )}
      <div className="px-4 py-2 border-t border-slate-200 bg-slate-50 rounded-b-xl text-xs text-slate-500 flex justify-between">
        <span>{shown.length} job{shown.length !== 1 ? 's' : ''}</span>
        {costVisible && <span>Running cost shown: <span className="font-semibold text-slate-800">{money(shownCost)}</span></span>}
      </div>
    </div>
  );
};
